// Browser shim for the Node `url` module's legacy API, aliased (in webpack-base.ts
// `resolve.alias`) in place of the removed `url` npm package — which was only pulled
// in to satisfy this, and dragged in a Veracode-flagged `qs`.
//
// The generated API client (src/generated/api.ts — auto-generated, not editable) uses
// ONLY `url.parse(path, true)` and `url.format(obj)`, always on RELATIVE paths like
// `/api/v1/...`. Native WHATWG `URL` throws on relative input, so the two legacy
// functions are implemented directly here. Query (de)serialization uses URLSearchParams.

export interface UrlObject {
    protocol?: string | null;
    slashes?: boolean | null;
    auth?: string | null;
    host?: string | null;
    hostname?: string | null;
    port?: string | null;
    hash?: string | null;
    search?: string | null;
    query?: Record<string, unknown> | string | null;
    pathname?: string | null;
    path?: string | null;
    href?: string | null;
}

/**
 * Legacy `url.parse`. Splits a (typically relative) URL into pathname/search/hash.
 * When `parseQueryString` is true, `query` is returned as a parsed object (matching
 * how api.ts calls it and then merges params into `urlObj.query`).
 */
export function parse(urlStr: string, parseQueryString?: boolean): UrlObject {
    const str = urlStr || '';

    const hashIndex = str.indexOf('#');
    const hash = hashIndex >= 0 ? str.slice(hashIndex) : null;
    const withoutHash = hashIndex >= 0 ? str.slice(0, hashIndex) : str;

    const qIndex = withoutHash.indexOf('?');
    const pathname = qIndex >= 0 ? withoutHash.slice(0, qIndex) : withoutHash;
    const search = qIndex >= 0 ? withoutHash.slice(qIndex) : null;

    let query: Record<string, string> | string | null;
    if (parseQueryString) {
        const parsed: Record<string, string> = {};
        if (search) {
            new URLSearchParams(search).forEach((value, key) => {
                parsed[key] = value;
            });
        }
        query = parsed;
    } else {
        query = search ? search.slice(1) : null;
    }

    return {
        protocol: null,
        slashes: null,
        auth: null,
        host: null,
        hostname: null,
        port: null,
        hash,
        search,
        query,
        pathname: pathname || null,
        path: `${pathname}${search || ''}`,
        href: str,
    };
}

/**
 * The removed `url` package resolved (via semver range) to a version backed by `qs`,
 * which serializes a `Date` via `toISOString()` rather than `String()` (which would
 * produce the useless `Date.prototype.toString()` form). Generated API client params
 * are typed to match backend `DateTime` query params, so callers do sometimes pass a
 * raw `Date` straight through — this restores that one behavior without pulling `qs`
 * (and its flagged transitive deps) back in.
 */
function stringifyQueryValue(value: unknown): string {
    return value instanceof Date ? value.toISOString() : String(value);
}

/**
 * Legacy `url.format`. api.ts mutates `urlObj.query` (not `search`), so when `query`
 * is an object it is the source of truth and the query string is rebuilt from it;
 * otherwise fall back to any existing `search`.
 */
export function format(urlObj: UrlObject | string): string {
    if (typeof urlObj === 'string') {
        return urlObj;
    }

    const pathname = urlObj.pathname || '';
    const hash = urlObj.hash || '';
    let search = urlObj.search || '';

    if (urlObj.query && typeof urlObj.query === 'object') {
        const params = new URLSearchParams();
        for (const key of Object.keys(urlObj.query)) {
            const value = (urlObj.query as Record<string, unknown>)[key];
            if (value === undefined || value === null) {
                continue;
            }
            if (Array.isArray(value)) {
                value.forEach((entry) => params.append(key, stringifyQueryValue(entry)));
            } else {
                params.append(key, stringifyQueryValue(value));
            }
        }
        const queryString = params.toString();
        search = queryString ? `?${queryString}` : '';
    } else if (typeof urlObj.query === 'string' && urlObj.query) {
        search = `?${urlObj.query}`;
    }

    return `${pathname}${search}${hash}`;
}

export default { parse, format };
